import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

const initial = {
  full_name: "",
  email: "",
  phone: "",
  event_type: "Wedding",
  event_date: "",
  guests: 50,
  venue_address: "",
  postcode: "",
  notes: "",
};

export default function PrivateBookingForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) =>
    setForm({
      ...form,
      [k]: k === "guests" ? parseInt(e.target.value || "0", 10) : e.target.value,
    });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads/private-booking", form);
      toast.success("Booking request sent.", {
        description: "We’ll confirm availability within 24 hours.",
      });
      setForm(initial);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Could not submit. Please check your details and try again.";
      toast.error("Submission failed", { description: String(detail) });
    } finally {
      setLoading(false);
    }
  };

  const input =
    "w-full bg-transparent border-b border-white/15 focus:border-[#8B5CF6] outline-none py-3 text-white placeholder:text-white/30 font-body transition-colors duration-200";
  const label = "font-mono-accent text-[10px] text-white/50 mb-1 block";

  return (
    <form
      data-testid="booking-form"
      onSubmit={submit}
      className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5"
    >
      <div>
        <label className={label}>Full name</label>
        <input
          data-testid="booking-name"
          required
          value={form.full_name}
          onChange={upd("full_name")}
          className={input}
          placeholder="Alex Jones"
        />
      </div>
      <div>
        <label className={label}>Email</label>
        <input
          data-testid="booking-email"
          type="email"
          required
          value={form.email}
          onChange={upd("email")}
          className={input}
          placeholder="you@email.com"
        />
      </div>
      <div>
        <label className={label}>Phone</label>
        <input
          data-testid="booking-phone"
          required
          value={form.phone}
          onChange={upd("phone")}
          className={input}
          placeholder="07..."
        />
      </div>
      <div>
        <label className={label}>Event type</label>
        <select
          data-testid="booking-event-type"
          value={form.event_type}
          onChange={upd("event_type")}
          className={`${input} appearance-none cursor-pointer`}
        >
          {["Wedding", "Birthday", "Corporate", "Festival", "Other"].map((o) => (
            <option key={o} value={o} className="bg-[#15161C]">
              {o}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className={label}>Event date</label>
        <input
          data-testid="booking-date"
          type="date"
          required
          value={form.event_date}
          onChange={upd("event_date")}
          className={`${input} [color-scheme:dark]`}
        />
      </div>
      <div>
        <label className={label}>Guests</label>
        <input
          data-testid="booking-guests"
          type="number"
          min={1}
          required
          value={form.guests}
          onChange={upd("guests")}
          className={input}
        />
      </div>
      <div className="md:col-span-2">
        <label className={label}>Venue address</label>
        <input
          data-testid="booking-venue"
          required
          value={form.venue_address}
          onChange={upd("venue_address")}
          className={input}
          placeholder="Venue name, street, city"
        />
      </div>
      <div className="md:col-span-2">
        <label className={label}>Postcode</label>
        <input
          data-testid="booking-postcode"
          required
          value={form.postcode}
          onChange={upd("postcode")}
          className={input}
          placeholder="E1 6AN"
        />
      </div>
      <div className="md:col-span-2">
        <label className={label}>Notes</label>
        <textarea
          data-testid="booking-notes"
          rows={2}
          value={form.notes}
          onChange={upd("notes")}
          className={`${input} resize-none`}
          placeholder="Timing, dietary requirements, branding..."
        />
      </div>

      <div className="md:col-span-2 mt-2">
        <button
          type="submit"
          disabled={loading}
          data-testid="booking-submit"
          className="w-full group inline-flex items-center justify-between rounded-full bg-[#8B5CF6] px-6 py-4 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 neon-glow-pink disabled:opacity-60 disabled:hover:translate-y-0"
        >
          {loading ? "Sending..." : "Request event booking"}
          <ArrowUpRight
            size={18}
            className="transition-transform duration-200 group-hover:rotate-45"
          />
        </button>
      </div>
    </form>
  );
}
